import { Box, IconButton, InputAdornment, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LastMessage from 'components/@home/@messages/ChannelsPanel/Channels/Channel/LastMessage';
import AttachmentButton from 'components/@home/@settings/Tabs/AttachmentButton';
import ChatFlowCaption from 'components/@home/@settings/Tabs/ChatFlowCaption';
import connector from 'components/@home/@settings/Tabs/connector';
import FormikChipInput from 'components/formik/ChipInput';
import TextField from 'components/formik/TextField';
import { FastField, Field, FieldArray, useFormikContext } from 'formik';
import DeleteIcon from 'mdi-react/DeleteIcon';
import { string } from 'prop-types';
import React, { useCallback, useMemo } from 'react';
import { I18n } from 'utils/i18n';

const INPUT_WRAPPER_CLASS = 'message-array-input-wrapper';
const DELETE_BTN_CLASS = 'message-array-delete-btn';

const getStyles = theme => ({
  root: {
    borderRadius: '6px',
    border: `1px solid ${theme.palette.secondary.light}`,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  attachment: {
    display: 'inline-flex',
    width: '100%',
    alignItems: 'center',
  },
  lastMessage: {
    padding: '10px',
    width: '100%',
  },
  inputWrapper: {
    [`& + &.${INPUT_WRAPPER_CLASS}`]: {
      borderTop: `1px solid ${theme.palette.secondary.light}`,
    },
    [`&:hover .${DELETE_BTN_CLASS}`]: {
      visibility: 'inherit',
    },
    [`&:hover:last-child .${DELETE_BTN_CLASS}`]: {
      visibility: 'hidden',
    },
  },
  input: {
    marginBottom: '-1px',
    paddingLeft: 0,
    paddingBottom: 0,
    paddingTop: 0,
    border: 0,
  },
  delete: {
    visibility: 'hidden',
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
  },
  field: {
    marginTop: theme.spacing(2),
  },
});

const MessageArray = ({ field }) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const { setFieldValue, getFieldProps } = useFormikContext();
  const { value: messages } = getFieldProps(field);
  if (messages.length === 0 || messages[messages.length - 1].text) {
    setFieldValue(field, [...messages, { _id: `${Math.random()}`, text: '' }]);
  }
  const render = useCallback(
    arrayHelpers => {
      const renderMessage = (message, idx) =>
        message.attachments?.[0] ? (
          <Typography
            component="div"
            className={INPUT_WRAPPER_CLASS}
            sx={[styles.inputWrapper, styles.attachment]}
          >
            <Box sx={styles.lastMessage}>
              <LastMessage
                lastMessage={{
                  ...message,
                  from: { _id: 1 },
                }}
              />
            </Box>
            <InputAdornment position="end" className={DELETE_BTN_CLASS} sx={styles.delete}>
              <IconButton aria-label="delete" onClick={() => arrayHelpers.remove(idx)} size="small">
                <DeleteIcon />
              </IconButton>
            </InputAdornment>
          </Typography>
        ) : (
          <FastField
            component={TextField}
            name={`${field}.${idx}.text`}
            placeholder={I18n.t('ChatFlowSettings.Bot says')}
            className={INPUT_WRAPPER_CLASS}
            slotProps={{
              input: {
                multiline: true,
                sx: styles.input,
                endAdornment: (
                  <>
                    {!messages[idx].text && (
                      <InputAdornment position="end">
                        <AttachmentButton field={`${field}.${idx}`} />
                      </InputAdornment>
                    )}
                    <InputAdornment position="end" className={DELETE_BTN_CLASS} sx={styles.delete}>
                      <IconButton
                        aria-label="delete"
                        onClick={() => arrayHelpers.remove(idx)}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </InputAdornment>
                  </>
                ),
              },
            }}
            sx={styles.inputWrapper}
          />
        );
      return messages.map(renderMessage);
    },
    [field, messages, styles],
  );
  return (
    <div>
      <Box sx={styles.root}>
        <FieldArray name={field} render={render} />
      </Box>
      <Typography variant="caption" color="textSecondary" component="p" sx={styles.field}>
        {I18n.t('ChatFlowSettings.Message variables hint')}
      </Typography>
      {messages.length > 1 && (
        <>
          <Typography sx={styles.heading}>{I18n.t('ChatFlowSettings.Suggestions')}</Typography>
          <ChatFlowCaption
            section="Suggestions"
            translationProps={{ count: messages.length - 1 }}
          />
          <Box sx={styles.field}>
            <Field
              component={FormikChipInput}
              placeholder={I18n.t('ChatFlowSettings.Add suggestions')}
              name={`${field}Suggestions`}
              creatable
            />
          </Box>
        </>
      )}
    </div>
  );
};

MessageArray.propTypes = {
  field: string.isRequired,
};

export default connector(MessageArray);
